<template>
    <div v-if="config" class="w-full flex flex-col items-start relative my-1">
        <Label :for="`control-${controlId}`" :class="['block text-sm font-medium', overrideLabelOpacity ? 'override-opacity' : '']">
            {{ props.config.display_name }}
        </Label>
        <div class="w-full mt-1 grid grid-cols-2 gap-2 items-start">
            <div class="relative">
                <Popover>
                    <PopoverTrigger as-child>
                        <Button :disabled="!editMode" variant="outline" class="w-full sm:w-[300px] justify-start text-left font-normal">
                            <CalendarIcon class="mr-2 h-4 w-4" />
                            <span v-if="controlValue.start">
                                {{ formatDate(controlValue.start) }}
                                <template v-if="controlValue.end"> - {{ formatDate(controlValue.end) }}</template>
                            </span>
                            <span v-else class="text-muted-foreground">Pick a date</span>
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent class="p-0" align="start">
                        <RangeCalendar
                            v-model="controlValue"
                            :range="props.config.range"
                            :number-of-months="props.config.range ? 2 : 1"
                            :disabled="!editMode" />
                    </PopoverContent>
                </Popover>
                <div v-if="errors.length" class="text-destructive mt-1 text-xs">
                    <template v-for="error in errors" :key="error.$uid">
                        <span>* {{ error.$message }}</span>
                    </template>
                </div>
            </div>
            <div class="relative">
                <Tooltip v-if="config.description && hideLateralDescriptionValue">
                    <TooltipTrigger asChild>
                        <span><Info class="h-4 w-4 text-muted-foreground" /></span>
                    </TooltipTrigger>
                    <TooltipContent side="left" class="whitespace-normal max-w-xs">
                        <p class="text-xs" v-html="sanitizeHtml(config.description)" />
                    </TooltipContent>
                </Tooltip>
                <Alert
                    v-if="config.description && !hideLateralDescriptionValue"
                    variant="default"
                    class="min-h-[calc(theme(spacing.10)+theme(spacing.6))] flex items-center p-3 text-xs border bg-muted/10">
                    <AlertDescription class="text-muted-foreground">
                        <span v-html="sanitizeHtml(config.description)" />
                    </AlertDescription>
                </Alert>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, onUpdated, onUnmounted } from 'vue';
import { RangeCalendar } from '@/components/ui/range-calendar';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { sanitizeHtml } from '@/utils/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info, Calendar as CalendarIcon } from 'lucide-vue-next';
import { getLocalTimeZone } from '@internationalized/date';
import { uuid4 } from '@sentry/utils';

const emit = defineEmits(['input']);
const controlId = uuid4();
const props = defineProps({
    config: {
        type: Object,
        required: true
    },
    value: {
        type: [Object, null],
        default: null
    },
    editMode: {
        type: Boolean,
        default: true
    },
    overrideLabelOpacity: {
        type: Boolean,
        default: false
    },
    hideLateralDescription: {
        type: Boolean,
        default: false
    },
    errors: {
        type: Array,
        default: () => []
    }
});

const controlValue = ref(props.value ?? { start: null, end: null });
const hideLateralDescriptionValue = ref(props.hideLateralDescription);

onUpdated(() => {
    hideLateralDescriptionValue.value = props.hideLateralDescription ? props.hideLateralDescription : !props.editMode || window.innerWidth <= 1024;
});

const handleResize = () => {
    hideLateralDescriptionValue.value = props.hideLateralDescription ? props.hideLateralDescription : window.innerWidth <= 1024;
};

onMounted(() => {
    handleResize();
});
window.addEventListener('resize', handleResize);
onUnmounted(() => window.removeEventListener('resize', handleResize));

watch(controlValue, (newValue) => {
    emit('input', newValue);
});

watch(
    () => props.value,
    (newValue) => {
        controlValue.value = newValue ?? { start: null, end: null };
    }
);

function formatDate(date) {
    return date.toDate(getLocalTimeZone()).toLocaleDateString();
}
</script>
